import lunr from "lunr"
import React, { useState, useEffect, useRef, FormEvent } from "react"
import { ResultItem } from "./result-item"
import { ToggleSearch } from "./toggle-search"

type SearchBarProps = {
  documents: Array<Document>
  lunrIndex: lunr.Index
  entityName: string
}

type Document = {
  path: string
  title: string
}

export const SearchBar = ({
  documents,
  lunrIndex,
  entityName,
}: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef(null)
  const [userInput, setUserInput] = useState("")
  const [showResults, setShowResults] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value)
  }

  const handleFormSubmission = (event: FormEvent) => {
    event.preventDefault()
  }

  let queryResult = searchDocuments(userInput)

  function searchDocuments(query: string) {
    let result: lunr.Index.Result[]

    // Handle search errors
    try {
      result = lunrIndex.search(query)
    } catch (error) {
      // Avoid breaking search when QueryParseError is thrown (special char input e.g ":")
      if (error instanceof lunr.QueryParseError) {
        // Remove special char from query
        let plainQuery = query.replace(/[^a-zA-Z0-9]/g, " ")
        result = lunrIndex.search(plainQuery)
      } else {
        result = []
      }
    }
    useEffect(() => {
      if (userInput !== "") {
        setShowResults(true)
      } else {
        setShowResults(false)
      }
    })
    return result.map(item => {
      return documents.find(document => item.ref === document.path)
    })
  }

  function ToggleSearchBar() {
    setSearchOpen(true)
  }

  useEffect(() => {
    if (searchOpen) {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }, [searchOpen])

  const closeSearchBar = () => {
    setUserInput("")
    setShowResults(false)
    setSearchOpen(false)
  }

  type MouseAndTouchEvents = MouseEvent | TouchEvent

  document.addEventListener("mousedown", function (event: MouseAndTouchEvents) {
    const inputEl = formRef?.current as HTMLInputElement | null
    if (!inputEl || inputEl.contains(event.target as Node)) {
      return
    }
    closeSearchBar()
  })

  document.addEventListener("keydown", function (event: KeyboardEvent) {
    if (event.keyCode === 27) {
      closeSearchBar()
    }
  })

  return (
    <div className="flex relative w-full tablet:w-auto">
      <ToggleSearch
        buttonDisabled={searchOpen}
        onClick={ToggleSearchBar}
        entityName={entityName}
      />
      <div
        data-test="search__container"
        className={`absolute top-0 left-0 right-0 tablet:right-auto bottom-0 tablet:bottom-auto ${
          searchOpen ? "block" : "hidden"
        }`}
        aria-expanded={searchOpen}
        aria-labelledby={`${entityName}-search-toggle`}
        id={`${entityName}-search`}
      >
        <div className={`${entityName}-search`}>
          <form
            onSubmit={handleFormSubmission}
            ref={formRef}
            className="relative"
            autoComplete="off"
          >
            <label
              htmlFor={`${entityName}-search-input`}
              className={
                userInput.length < 1
                  ? "sr-only"
                  : "absolute -top-20 text-12 text-grey"
              }
            >
              Search the Culture Amp blog
            </label>
            <input
              data-test="search__input"
              ref={inputRef}
              className={`border border-black py-10 px-12 text-14 leading-tight bg-white w-full font-main outline-none appearance-none focus:outline-dark rounded-none placeholder-black-60 tablet:w-[430px] ${entityName}-search__input`}
              id={`${entityName}-search-input`}
              value={userInput}
              placeholder={`Search ${entityName}`}
              type="search"
              role="combobox"
              aria-autocomplete="list"
              aria-activedescendant="suggestion-0"
              onChange={handleInputChange}
            />
            <div
              data-test="search__results"
              aria-expanded={showResults}
              id={`${entityName}-search-suggestions`}
              className={`border border-black bg-white shadow-sm overflow-hidden absolute left-0 right-0 top-44 -mt-6 z-40 ${entityName}-search__results
        ${showResults ? "block" : "hidden"}`}
            >
              <ul role="listbox" className="overflow-y-scroll max-h-[259px]">
                {queryResult.length === 0 && (
                  <li
                    className="border-b border-black-20"
                    role="option"
                    id="suggestion-0"
                  >
                    <span className="flex items-center text-14 truncate px-12 py-16">
                      No {entityName} found
                    </span>
                  </li>
                )}
                {queryResult.map((result, index) => {
                  if (result && userInput !== "") {
                    return (
                      <ResultItem
                        key={result.path}
                        title={result.title}
                        path={result.path}
                        index={index}
                      />
                    )
                  } else {
                    return null
                  }
                })}
              </ul>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
